import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { getOwner } from '@ember/application';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! «UI nuova»: a 40px field (swatch + hex) and the panel in a <body> wormhole with fixed coordinates\n     (bb-color-picker.js placePanel), never clipped by an overflow:hidden card. Classic: the markup below the else,\n     unchanged. }}\n<div\n  class=\'color-picker {{if this.isNuova \"nv-cp\"}} {{if this.isOpen \"is-open\"}}\'\n  {{did-insert this.registerOutside}}\n  {{will-destroy this.unregisterOutside}}\n  ...attributes\n>\n  {{#if this.isNuova}}\n    <button\n      type=\'button\'\n      class=\'nv-cp-field\'\n      title={{t \'color\'}}\n      aria-haspopup=\'dialog\'\n      aria-expanded={{if this.isOpen \'true\' \'false\'}}\n      data-test-bb-color-picker-toggle\n      {{on \'click\' this.toggle}}\n    >\n      <span\n        class=\'color-picker__trigger {{unless this.hasColor \"is-empty\"}}\'\n        style={{this.triggerStyle}}\n        aria-hidden=\'true\'\n      ></span>\n      <span class=\'nv-cp-hex\' data-test-nv-cp-hex>\n        {{this.hexLabel}}\n      </span>\n    </button>\n\n    {{#if this.isOpen}}\n      {{#in-element this.destinationElement insertBefore=null}}\n        <div\n          class=\'color-picker__panel nv-cp-pop\'\n          role=\'dialog\'\n          aria-label={{t \'color\'}}\n          data-nv-layer\n          data-test-nv-cp-pop\n          {{did-insert this.placePanel}}\n          {{will-destroy this.releasePanel}}\n          {{on \'focusout\' this.panelFocusOut}}\n        >\n          <div class=\'color-picker__grid\'>\n            {{#each this.swatches as |swatch|}}\n              <button\n                type=\'button\'\n                class=\'color-picker__swatch {{if swatch.selected \"is-selected\"}}\'\n                style={{swatch.style}}\n                title={{swatch.hex}}\n                aria-label={{swatch.hex}}\n                data-test-nv-cp-current={{swatch.current}}\n                aria-pressed={{if swatch.selected \'true\' \'false\'}}\n                data-test-select-color\n                {{on \'click\' (fn this.selectColor swatch.hex)}}\n              ></button>\n            {{/each}}\n          </div>\n\n          <div class=\'color-picker__footer\'>\n            <label class=\'color-picker__custom\' title={{t \'color\'}}>\n              <input\n                type=\'color\'\n                value={{this.customValue}}\n                aria-label={{t \'color\'}}\n                data-test-on-custom-input\n                {{on \'input\' this.onCustomInput}}\n              />\n            </label>\n\n            <button\n              type=\'button\'\n              class=\'color-picker__remove\'\n              title={{t \'reset default color\'}}\n              aria-label={{t \'reset default color\'}}\n              data-test-remove\n              {{on \'click\' this.remove}}\n            >\n              <FaIcon @icon=\'trash-alt\' />\n            </button>\n          </div>\n        </div>\n      {{/in-element}}\n    {{/if}}\n  {{else}}\n    <button\n      type=\'button\'\n      class=\'color-picker__trigger {{unless this.hasColor \"is-empty\"}}\'\n      style={{this.triggerStyle}}\n      title={{t \'color\'}}\n      data-test-bb-color-picker-toggle\n      {{on \'click\' this.toggle}}\n    ></button>\n\n    {{#if this.isOpen}}\n      <div class=\'color-picker__panel\'>\n        <div class=\'color-picker__grid\'>\n          {{#each this.swatches as |swatch|}}\n            <button\n              type=\'button\'\n              class=\'color-picker__swatch {{if swatch.selected \"is-selected\"}}\'\n              style={{swatch.style}}\n              title={{swatch.hex}}\n              data-test-select-color\n              {{on \'click\' (fn this.selectColor swatch.hex)}}\n            ></button>\n          {{/each}}\n        </div>\n\n        <div class=\'color-picker__footer\'>\n          <label class=\'color-picker__custom\' title={{t \'color\'}}>\n            <input\n              type=\'color\'\n              value={{this.customValue}}\n              data-test-on-custom-input\n              {{on \'input\' this.onCustomInput}}\n            />\n          </label>\n\n          <button\n            type=\'button\'\n            class=\'color-picker__remove\'\n            title={{t \'reset default color\'}}\n            data-test-remove\n            {{on \'click\' this.remove}}\n          >\n            <FaIcon @icon=\'trash-alt\' />\n          </button>\n        </div>\n      </div>\n    {{/if}}\n  {{/if}}\n</div>");

var _class, _descriptor;
const DEFAULT_COLORS = [
// greys
'#000000', '#444444', '#666666', '#999999', '#cccccc', '#eeeeee', '#f3f3f3', '#ffffff',
// primaries
'#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#9900ff', '#ff00ff',
// tint/shade ramps (lightest -> darkest)
'#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c6', '#674ea7', '#a64d79', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#0b5394', '#351c75', '#741b47', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#073763', '#20124d', '#4c1130'];

// «UI nuova» default palette (owner decision 2026-10-09): 40 well-spread swatches, 5 rows × 8 columns — the neutral
// row, then red / orange / yellow / green / teal / blue / purple / magenta in four steps: light tint, vivid, medium,
// dark. Every value is also in DEFAULT_COLORS (classic), so a colour picked here still reads as selected in classic.
// A stored colour outside the palette is shown as an extra selected swatch (see `swatches`).
const NUOVA_COLORS = [
// neutrals
'#000000', '#444444', '#666666', '#999999', '#cccccc', '#eeeeee', '#f3f3f3', '#ffffff',
// light tints
'#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd',
// vivid
'#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#9900ff', '#ff00ff',
// medium
'#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c6', '#674ea7', '#a64d79',
// dark
'#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#0b5394', '#351c75', '#741b47'];

// «UI nuova» panel placement: distance from the field, minimum margin from the viewport edges.
const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 8;
const WORMHOLE_ID = 'bb-color-picker-wormhole';

/**
 * «UI nuova» panel coordinates (viewport / position:fixed px). Under the field, left-aligned with it; above it when
 * it does not fit below but fits above; otherwise pinned to the bottom margin. Always clamped inside the viewport.
 * Pure: `field` is a DOMRect-like {top, bottom, left}.
 */
function colourPanelPosition({
  field,
  width,
  height,
  viewportWidth,
  viewportHeight
}) {
  let top = field.bottom + PANEL_GAP;
  let placement = 'bottom';
  if (top + height > viewportHeight - VIEWPORT_MARGIN) {
    const above = field.top - PANEL_GAP - height;
    if (above >= VIEWPORT_MARGIN) {
      top = above;
      placement = 'top';
    } else {
      top = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - height);
    }
  }
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width);
  const left = Math.min(Math.max(field.left, VIEWPORT_MARGIN), maxLeft);
  return {
    top: Math.round(top),
    left: Math.round(left),
    placement
  };
}

/**
 * Native, dependency-free color picker.
 *
 * Collapsed by default: shows a single trigger with the currently selected
 * color, or a black/white square when nothing is selected (signalling the user
 * must click to choose). Clicking opens the preset swatches plus a native
 * `<input type="color">` for the full palette and a remove (trash) action.
 * Picking a swatch or removing collapses the panel.
 *
 * Args:
 *  - `@color`     current value (hex string)
 *  - `@onChange`  called with the new hex (or null when removed)
 *  - `@palette`   optional array of hex strings to replace the defaults
 */
let BbColorPickerComponent = (_class = class BbColorPickerComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "isOpen", _descriptor, this);
    this._onOutside = null;
    this._rootElement = null;
    this._panelElement = null;
    this._destination = null;
    this._onPanelKeydown = null;
    this._onViewportChange = null;
  }
  get isNuova() {
    return getOwner(this)?.lookup('service:ui')?.isNuova === true;
  }

  // «UI nuova»: the panel renders in a <body> wormhole (created on first open), outside every clipping ancestor.
  get destinationElement() {
    return this._destination;
  }

  // «UI nuova» field text: the hex of the chosen colour (empty when none).
  get hexLabel() {
    return this.args.color ? String(this.args.color).toUpperCase() : '';
  }

  // classic keeps the 64 colours; nuova the shorter 40 (both overridable with @palette)
  get colors() {
    return this.args.palette || (this.isNuova ? NUOVA_COLORS : DEFAULT_COLORS);
  }
  get hasColor() {
    return !!this.args.color;
  }

  // Solid background for the trigger when a color is selected; empty otherwise
  // (CSS paints the black/white "no color" affordance).
  get triggerStyle() {
    return this.hasColor ? htmlSafe(`background-color: ${this.args.color}`) : undefined;
  }

  // Native <input type="color"> requires a valid #rrggbb value.
  get customValue() {
    return this.args.color || '#000000';
  }

  // «UI nuova»: a stored colour that is not in the palette is appended as one extra selected swatch («current»), so
  // opening the picker never hides the value in use. Classic: the palette only, unchanged.
  get swatches() {
    const current = (this.args.color || '').toLowerCase();
    const swatches = this.colors.map(hex => ({
      hex,
      selected: hex.toLowerCase() === current,
      style: htmlSafe(`background-color: ${hex}`)
    }));
    if (this.isNuova && current && !swatches.some(s => s.selected)) {
      const hex = String(this.args.color);
      swatches.push({
        hex,
        selected: true,
        current: true,
        style: htmlSafe(`background-color: ${hex}`)
      });
    }
    return swatches;
  }
  update(color) {
    this.args.onChange?.(color);
  }
  registerOutside(element) {
    this._rootElement = element;
    this._onOutside = event => {
      if (!this.isOpen || element.contains(event.target)) return;
      // nuova: the panel lives in the body wormhole, outside `element`
      if (this._panelElement?.contains(event.target)) return;
      this.isOpen = false;
    };
    // Capture phase so this runs before the trigger's own click handler.
    document.addEventListener('click', this._onOutside, true);
  }
  unregisterOutside() {
    if (this._onOutside) {
      document.removeEventListener('click', this._onOutside, true);
    }
    this._rootElement = null;
  }
  toggle() {
    // re-resolved when detached (a test / shell teardown can drop the wormhole from <body>)
    if (!this.isOpen && this.isNuova && !this._destination?.isConnected) {
      this._destination = this._wormhole();
    }
    this.isOpen = !this.isOpen;
  }
  _wormhole() {
    let el = document.getElementById(WORMHOLE_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = WORMHOLE_ID;
      document.body.appendChild(el);
    }
    return el;
  }

  // ── «UI nuova» floating panel ──────────────────────────────────────────────────────────────────────────────────

  placePanel(panel) {
    this._panelElement = panel;
    this._onViewportChange = () => this._place();
    this._onPanelKeydown = event => {
      if (event.key !== 'Escape' || !this.isOpen) return;
      // the panel owns this Escape: the modal / record under it must not close too
      event.stopPropagation();
      event.preventDefault();
      this.isOpen = false;
      this._rootElement?.querySelector('.nv-cp-field')?.focus();
    };
    document.addEventListener('keydown', this._onPanelKeydown, true);
    window.addEventListener('resize', this._onViewportChange);
    window.addEventListener('scroll', this._onViewportChange, true);
    this._place();
    // keyboard users land in the palette: the chosen swatch, else the first one
    (panel.querySelector('.color-picker__swatch.is-selected') || panel.querySelector('.color-picker__swatch'))?.focus({
      preventScroll: true
    });
  }
  releasePanel() {
    document.removeEventListener('keydown', this._onPanelKeydown, true);
    window.removeEventListener('resize', this._onViewportChange);
    window.removeEventListener('scroll', this._onViewportChange, true);
    this._onPanelKeydown = null;
    this._onViewportChange = null;
    this._panelElement = null;
  }

  // Tabbing out of the panel (to anything but the field) closes it.
  panelFocusOut(event) {
    const next = event.relatedTarget;
    if (!next || this._panelElement?.contains(next) || this._rootElement?.contains(next)) return;
    this.isOpen = false;
  }

  // Fixed coordinates: under the field, left-aligned; above it when it does not fit below and fits above; always
  // clamped inside the viewport (a phone at 375px included).
  _place() {
    const panel = this._panelElement;
    const field = this._rootElement;
    if (!panel || !field) return;
    const {
      top,
      left,
      placement
    } = colourPanelPosition({
      field: field.getBoundingClientRect(),
      width: panel.offsetWidth,
      height: panel.offsetHeight,
      viewportWidth: document.documentElement.clientWidth || window.innerWidth,
      viewportHeight: window.innerHeight
    });
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.dataset.placement = placement;
  }
  selectColor(hex) {
    this.update(hex);
    this.isOpen = false;
  }
  onCustomInput(event) {
    this.update(event.target.value);
  }
  remove() {
    this.update(null);
    this.isOpen = false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "isOpen", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "registerOutside", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerOutside"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unregisterOutside", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unregisterOutside"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "placePanel", [action], Object.getOwnPropertyDescriptor(_class.prototype, "placePanel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "releasePanel", [action], Object.getOwnPropertyDescriptor(_class.prototype, "releasePanel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "panelFocusOut", [action], Object.getOwnPropertyDescriptor(_class.prototype, "panelFocusOut"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectColor", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectColor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onCustomInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onCustomInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "remove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "remove"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbColorPickerComponent);

export { NUOVA_COLORS, colourPanelPosition, BbColorPickerComponent as default };
//# sourceMappingURL=bb-color-picker.js.map
